/**
 * Inline-style bundles for OwnerDashboard home tab — light vs dark (html.theme-dark).
 * Keeps contrast readable when Appearance → Dark theme is enabled.
 */
export function getOwnerDashboardSkin(isDark) {
  if (!isDark) {
    return {
      heroWrap: {
        marginBottom: '22px',
        background: 'linear-gradient(135deg, #eff6ff 0%, #f8fafc 45%, #ecfeff 100%)',
        border: '1px solid #dbeafe',
        borderRadius: '16px',
        padding: '24px 26px',
        boxShadow: '0 8px 24px rgba(37,99,235,0.08)',
      },
      heroKicker: { margin: '0 0 8px 0', fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#2563eb' },
      heroTitle: { margin: '0 0 10px 0', fontWeight: 800, color: '#0f172a', fontSize: '2rem' },
      heroSub: { margin: 0, maxWidth: '720px', fontSize: '15px', lineHeight: '1.6', color: '#475569' },
      qaCard: {
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '18px 20px',
        marginBottom: '26px',
        boxShadow: '0 2px 8px rgba(15,23,42,0.03)',
      },
      qaHeading: { margin: '0', fontSize: '16px', fontWeight: 600, color: '#0f172a' },
      qaSub: { margin: '4px 0 0 0', fontSize: '12px', color: '#94a3b8' },
      qaToggleBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 14px',
        borderRadius: 8,
        border: '1px solid #e2e8f0',
        background: '#fff',
        cursor: 'pointer',
      },
      qaToggleLabel: { fontWeight: 700, color: '#0f172a' },
      qaToggleChevron: { fontSize: 12, color: '#64748b' },
      qaMenuItemRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        cursor: 'pointer',
        borderBottom: '1px solid #f1f5f9',
      },
      qaMenuItemLabel: { fontSize: 14, color: '#0f172a' },
      kpiTile: {
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        padding: '18px',
        boxShadow: '0 1px 4px rgba(15,23,42,0.03)',
      },
      kpiTileClickable: { cursor: 'pointer', transition: 'box-shadow 0.15s' },
      kpiLabel: { margin: 0, fontSize: '12px', color: '#64748b', fontWeight: 600, letterSpacing: '0.04em' },
      kpiValue: { margin: '8px 0 0 0', fontSize: '26px', fontWeight: '700', color: '#1e293b' },
      recentCard: {
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '20px 24px',
        boxShadow: '0 2px 8px rgba(15,23,42,0.04)',
        minWidth: 0,
        maxWidth: '100%',
        width: '100%',
        boxSizing: 'border-box',
      },
      recentTitle: { margin: 0, fontSize: '16px', fontWeight: 600, color: '#1e293b' },
      recentViewAll: { background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
      recentEmpty: { margin: 0, color: '#6b7280' },
      recentItem: { padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer' },
      recentItemTitle: { margin: 0, fontWeight: '600', color: '#111827' },
      recentItemMeta: { margin: '4px 0 0 0', fontSize: '13px', color: '#6b7280' },
      addUnitPageTitle: { margin: '0 0 12px 0', fontSize: '24px', fontWeight: 700, color: '#0f172a' },
      addUnitFormShell: {
        background: '#f0f8ff',
        padding: '20px',
        borderRadius: '8px',
        border: '2px solid #007bff',
      },
      addUnitSectionTitle: { margin: '0 0 15px', fontWeight: 600, color: '#1e293b' },
      addUnitSectionTitleTight: { margin: '0 0 5px', fontWeight: 600, color: '#1e293b' },
      addUnitSectionTitleMuted: { fontWeight: 400, color: '#64748b', fontSize: '13px' },
      addUnitHelpText: { margin: '0 0 15px', color: '#64748b', fontSize: '13px' },
      addUnitDivider: { marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #bfdbfe' },
      addUnitLeaseLabel: {
        fontSize: '12px',
        color: '#64748b',
        fontWeight: 600,
        display: 'block',
        marginBottom: '4px',
      },
      addUnitDocsButton: {
        background: '#fff',
        border: '2px dashed #93c5fd',
        color: '#3b82f6',
        padding: '10px 20px',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        width: '100%',
        marginBottom: '10px',
      },
      addUnitDocRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '6px 10px',
        background: '#eff6ff',
        borderRadius: '6px',
        marginBottom: '6px',
        fontSize: '13px',
        color: '#1e293b',
      },
      addUnitVacantPanel: {
        marginTop: '20px',
        padding: '16px',
        background: '#f8fafc',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
      },
      addUnitVacantTitle: { margin: '0 0 4px 0', fontWeight: 600, color: '#1e293b' },
      addUnitVacantDesc: { margin: 0, color: '#64748b', fontSize: '13px' },
      addUnitNotesLabel: { display: 'block', fontWeight: 600, marginBottom: '8px', color: '#1e293b' },
    };
  }

  return {
    heroWrap: {
      marginBottom: '22px',
      background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 45%, #134e4a 100%)',
      border: '1px solid #312e81',
      borderRadius: '16px',
      padding: '24px 26px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
    },
    heroKicker: { margin: '0 0 8px 0', fontSize: '12px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#93c5fd' },
    heroTitle: { margin: '0 0 10px 0', fontWeight: 800, color: '#f8fafc', fontSize: '2rem' },
    heroSub: { margin: 0, maxWidth: '720px', fontSize: '15px', lineHeight: '1.6', color: '#94a3b8' },
    qaCard: {
      background: '#1e293b',
      border: '1px solid #334155',
      borderRadius: '12px',
      padding: '18px 20px',
      marginBottom: '26px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    },
    qaHeading: { margin: '0', fontSize: '16px', fontWeight: 600, color: '#f1f5f9' },
    qaSub: { margin: '4px 0 0 0', fontSize: '12px', color: '#94a3b8' },
    qaToggleBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '10px 14px',
      borderRadius: 8,
      border: '1px solid #475569',
      background: '#0f172a',
      cursor: 'pointer',
    },
    qaToggleLabel: { fontWeight: 700, color: '#f1f5f9' },
    qaToggleChevron: { fontSize: 12, color: '#94a3b8' },
    qaMenuItemRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '10px 12px',
      cursor: 'pointer',
      borderBottom: '1px solid #334155',
    },
    qaMenuItemLabel: { fontSize: 14, color: '#e2e8f0' },
    kpiTile: {
      background: '#1e293b',
      border: '1px solid #334155',
      borderRadius: '10px',
      padding: '18px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
    },
    kpiTileClickable: { cursor: 'pointer', transition: 'box-shadow 0.15s' },
    kpiLabel: { margin: 0, fontSize: '12px', color: '#94a3b8', fontWeight: 600, letterSpacing: '0.04em' },
    kpiValue: { margin: '8px 0 0 0', fontSize: '26px', fontWeight: '700', color: '#f8fafc' },
    recentCard: {
      background: '#1e293b',
      border: '1px solid #334155',
      borderRadius: '12px',
      padding: '20px 24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      minWidth: 0,
      maxWidth: '100%',
      width: '100%',
      boxSizing: 'border-box',
    },
    recentTitle: { margin: 0, fontSize: '16px', fontWeight: 600, color: '#f1f5f9' },
    recentViewAll: { background: 'none', border: 'none', color: '#93c5fd', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
    recentEmpty: { margin: 0, color: '#94a3b8' },
    recentItem: { padding: '10px', border: '1px solid #475569', borderRadius: '8px', cursor: 'pointer', background: '#0f172a' },
    recentItemTitle: { margin: 0, fontWeight: '600', color: '#f1f5f9' },
    recentItemMeta: { margin: '4px 0 0 0', fontSize: '13px', color: '#94a3b8' },
    addUnitPageTitle: { margin: '0 0 12px 0', fontSize: '24px', fontWeight: 700, color: '#f8fafc' },
    addUnitFormShell: {
      background: '#1e293b',
      padding: '20px',
      borderRadius: '8px',
      border: '2px solid #3b82f6',
    },
    addUnitSectionTitle: { margin: '0 0 15px', fontWeight: 600, color: '#f1f5f9' },
    addUnitSectionTitleTight: { margin: '0 0 5px', fontWeight: 600, color: '#f1f5f9' },
    addUnitSectionTitleMuted: { fontWeight: 400, color: '#94a3b8', fontSize: '13px' },
    addUnitHelpText: { margin: '0 0 15px', color: '#cbd5e1', fontSize: '13px' },
    addUnitDivider: { marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #334155' },
    addUnitLeaseLabel: {
      fontSize: '12px',
      color: '#94a3b8',
      fontWeight: 600,
      display: 'block',
      marginBottom: '4px',
    },
    addUnitDocsButton: {
      background: '#0f172a',
      border: '2px dashed #475569',
      color: '#93c5fd',
      padding: '10px 20px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      width: '100%',
      marginBottom: '10px',
    },
    addUnitDocRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '6px 10px',
      background: '#0f172a',
      borderRadius: '6px',
      marginBottom: '6px',
      fontSize: '13px',
      color: '#e2e8f0',
      border: '1px solid #334155',
    },
    addUnitVacantPanel: {
      marginTop: '20px',
      padding: '16px',
      background: '#0f172a',
      borderRadius: '10px',
      border: '1px solid #334155',
    },
    addUnitVacantTitle: { margin: '0 0 4px 0', fontWeight: 600, color: '#f1f5f9' },
    addUnitVacantDesc: { margin: 0, color: '#94a3b8', fontSize: '13px' },
    addUnitNotesLabel: { display: 'block', fontWeight: 600, marginBottom: '8px', color: '#f1f5f9' },
  };
}
